import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, currentCompanyName } from '../firebase/config';
import { useResponsive } from '../utils/useResponsive';
import { MobileFormEnhancements } from './MobileOptimization';
import { logoStorageService } from '../services/storageService';
import { usersService, ensureUserDoc } from '../services/firestoreService';
import { configHelpers } from '../config/app';
import { setCompanyFavicon } from '../utils/favicon';
import '../styles/mobile-optimization.css';

// Admin user credentials (hardcoded fallback)
const ADMIN_CREDENTIALS = {
  'owner': 'owner'
};

// Default Plutoo logo SVG component
const PlutooLogoSVG = ({ className = '', style = {} }) => (
  <svg 
    viewBox="0 0 100 120" 
    className={className}
    style={style}
  >
    {/* Black circle with white center (donut shape) */}
    <circle cx="50" cy="40" r="30" fill="#000000" />
    <circle cx="50" cy="40" r="18" fill="#ffffff" />
    {/* Cyan rectangle below */}
    <rect x="30" y="80" width="40" height="12" fill="#00bcd4" rx="2" />
  </svg>
);

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDesktopMessage, setShowDesktopMessage] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { isMobile, isTablet, isTouchDevice } = useResponsive();

  useEffect(() => {
    // Show desktop recommendation only for actual mobile/tablet devices
    // Check if it's a touch device AND screen is small (not just a narrow desktop window)
    // This prevents showing on desktop browsers with narrow windows
    if ((isMobile || isTablet) && isTouchDevice) {
      setShowDesktopMessage(true);
    }

    // Load saved credentials if "Remember Me" was checked
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedUsername && savedRememberMe) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    // Load logo from Firestore only - no localStorage fallback
    const loadLogo = async () => {
      try {
        const url = await logoStorageService.getLogoURL();
        if (url) {
          setLogoUrl(url);
          setCompanyFavicon(url);
          console.log('Login: Logo loaded from Firestore:', url);
        } else {
          // No custom logo found - will use default Plutoo SVG logo
          setLogoUrl('');
          console.log('Login: No custom logo found, using default Plutoo SVG logo');
        }
      } catch (error) {
        console.error('Error loading logo from Firestore:', error);
        // Use default Plutoo SVG logo on error
        setLogoUrl('');
        console.log('Login: Error loading logo, using default Plutoo SVG logo');
      }
    };
    loadLogo();

    // Listen for branding updates (when logo is changed in settings)
    const handleBrandingUpdate = (e) => {
      if (e.detail.logo !== undefined) {
        setLogoUrl(e.detail.logo || '');
        if (e.detail.logo) setCompanyFavicon(e.detail.logo);
        console.log('Login: Logo updated via event:', e.detail.logo || 'default Plutoo SVG logo');
      }
    };
    window.addEventListener('brandingUpdated', handleBrandingUpdate);

    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
    };
  }, [isMobile, isTablet, isTouchDevice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!username || !password) {
      setError('Please enter both username/email and password');
      setIsLoading(false);
      return;
    }

    // Handle "Remember Me" functionality
    if (rememberMe) {
      localStorage.setItem('rememberedUsername', username);
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberedUsername');
      localStorage.removeItem('rememberMe');
    }

    console.log('🚀 Login attempt started:', {
      username: username,
      isEmail: username.includes('@'),
      timestamp: new Date().toISOString()
    });

    try {
      // First, check if it's the admin user (hardcoded fallback)
      if (ADMIN_CREDENTIALS[username] && ADMIN_CREDENTIALS[username] === password) {
        console.log('✅ Admin user login successful');
        sessionStorage.setItem('authenticated', 'true');
        sessionStorage.setItem('username', username);
        sessionStorage.setItem('authType', 'admin');
        sessionStorage.setItem('userId', 'admin');
        onLogin();
        setIsLoading(false);
        return;
      }

      // Determine if input is email or username
      const isEmail = username.includes('@');
      let email = username;
      let firestoreUser = null;

      // If it's a username (not email), look up the user in Firestore first to get their email
      if (!isEmail) {
        console.log('🔍 Input is username, looking up user in Firestore...', username);
        try {
          firestoreUser = await usersService.getByUsername(username);
          if (firestoreUser) {
            console.log('✅ User found in Firestore:', {
              id: firestoreUser.id,
              username: firestoreUser.username,
              email: firestoreUser.email,
              active: firestoreUser.active,
              role: firestoreUser.role
            });
            
            // Check if account is active
            if (firestoreUser.active === false) {
              console.error('❌ Account is disabled');
              setError('This account has been disabled. Please contact your administrator.');
              setIsLoading(false);
              return;
            }
            
            // Get the email from Firestore user
            if (firestoreUser.email) {
              email = firestoreUser.email;
              console.log('✅ Found user in Firestore, using email:', email);
            } else {
              // Fallback to generated email if no email stored
              email = `${username}@${currentCompanyName}.local`;
              console.log('⚠️ No email in Firestore, using generated email:', email);
            }
          } else {
            // Username not found, try with generated email
            email = `${username}@${currentCompanyName}.local`;
            console.log('⚠️ Username not found in Firestore, trying generated email:', email);
          }
        } catch (firestoreError) {
          console.error('❌ Error looking up user in Firestore:', {
            code: firestoreError.code,
            message: firestoreError.message,
            fullError: firestoreError
          });
          // Continue with generated email as fallback
          email = `${username}@${currentCompanyName}.local`;
        }
      }

      console.log('🔐 Attempting Firebase login with email:', email);
      console.log('📋 Firebase Auth Config:', {
        projectId: auth.app.options.projectId,
        authDomain: auth.app.options.authDomain
      });
      
      try {
        // Try Firebase Authentication with the email (from username lookup or direct email)
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        console.log('✅ Firebase login successful:', {
          email: user.email,
          uid: user.uid
        });
        
        // Ensure Firestore user doc at /users/{uid} exists with companyId so this user can see company data (vehicles, invoices)
        try {
          await ensureUserDoc(user.uid, { email: user.email || '', username: username });
        } catch (err) {
          console.error('ensureUserDoc on login:', err);
        }
        
        // If we looked up the user by username, get their username from Firestore
        // Otherwise, if they logged in with email, try to find their username
        let userUsername = username;
        if (!firestoreUser && isEmail) {
          // User logged in with email, try to find username in Firestore
          try {
            const userByEmail = await usersService.getByEmail(email);
            if (userByEmail && userByEmail.username) {
              userUsername = userByEmail.username;
            }
          } catch (err) {
            console.log('Could not find username for email, using email as username');
          }
        } else if (firestoreUser) {
          // We already have the user from Firestore lookup
          userUsername = firestoreUser.username;
        }
        
        // Store authentication info
        sessionStorage.setItem('authenticated', 'true');
        sessionStorage.setItem('username', userUsername);
        sessionStorage.setItem('authType', 'firebase');
        sessionStorage.setItem('userId', user.uid);
        sessionStorage.setItem('userEmail', user.email);
        
        // Store role if available from Firestore
        // Auto-set admin role for admin email domain (god mode)
        let userRole = firestoreUser?.role;
        if (configHelpers.isAdminEmail(user.email)) {
          userRole = 'admin';
          console.log('Auto-setting admin role for admin email domain on login:', user.email);
          // Update role in Firestore if it's not already admin
          if (firestoreUser && firestoreUser.role !== 'admin') {
            try {
              await usersService.update(firestoreUser.id, { role: 'admin' });
              console.log('Updated user role to admin in Firestore on login');
            } catch (err) {
              console.error('Error updating role to admin on login:', err);
            }
          }
        }
        if (userRole) {
          sessionStorage.setItem('userRole', userRole);
        }
        
        setIsLoading(false);
        onLogin();
        return;
      } catch (firebaseError) {
        // Log the full error for debugging
        console.error('❌ Firebase authentication error:', {
          code: firebaseError.code,
          message: firebaseError.message,
          email: email,
          projectId: auth.app.options.projectId
        });
        
        // If Firebase Auth fails, try Firestore login as fallback (for users without Firebase Auth)
        if (firebaseError.code === 'auth/user-not-found' || firebaseError.code === 'auth/invalid-credential') {
          console.log('🔄 Firebase Auth failed, trying Firestore login as fallback...');
          
          // If we haven't looked up the user yet, do it now
          // Try by username if input was username, or by email if input was email
          if (!firestoreUser) {
            try {
              if (isEmail) {
                // User logged in with email, look up by email in Firestore
                console.log('🔍 Looking up user in Firestore by email:', email);
                firestoreUser = await usersService.getByEmail(email);
              } else {
                // User logged in with username, look up by username in Firestore
                console.log('🔍 Looking up user in Firestore by username:', username);
                firestoreUser = await usersService.getByUsername(username);
              }
              
              if (firestoreUser) {
                console.log('✅ User found in Firestore during fallback:', {
                  id: firestoreUser.id,
                  username: firestoreUser.username,
                  email: firestoreUser.email,
                  active: firestoreUser.active,
                  hasPassword: !!firestoreUser.password
                });
              } else {
                console.log('⚠️ User not found in Firestore during fallback');
              }
            } catch (err) {
              console.error('❌ Error looking up user in Firestore during fallback:', {
                code: err.code,
                message: err.message,
                fullError: err
              });
            }
          }
          
          if (firestoreUser) {
            console.log('🔍 User found in Firestore, checking password...');
            console.log('🔑 Password check:', {
              storedPassword: firestoreUser.password ? '***' : 'NOT SET',
              enteredPassword: password ? '***' : 'NOT SET',
              match: firestoreUser.password === password
            });
            
            // Check password stored in Firestore (for backward compatibility)
            if (firestoreUser.password === password) {
              if (firestoreUser.active === false) {
                setError('This account has been disabled. Please contact your administrator.');
                setIsLoading(false);
                return;
              }
              
              console.log('✅ User authenticated via Firestore (fallback):', firestoreUser.username);
              
              // Auto-set admin role for admin email domain (god mode)
              let userRole = firestoreUser.role;
              if (configHelpers.isAdminEmail(firestoreUser.email)) {
                userRole = 'admin';
                console.log('🔑 Auto-setting admin role for admin email domain on Firestore login:', firestoreUser.email);
                // Update role in Firestore if it's not already admin
                if (firestoreUser.role !== 'admin') {
                  try {
                    await usersService.update(firestoreUser.id, { role: 'admin' });
                    console.log('✅ Updated user role to admin in Firestore on Firestore login');
                  } catch (err) {
                    console.error('❌ Error updating role to admin on Firestore login:', err);
                  }
                }
              }
              
              // Store authentication info
              sessionStorage.setItem('authenticated', 'true');
              sessionStorage.setItem('username', firestoreUser.username);
              sessionStorage.setItem('authType', 'firestore');
              sessionStorage.setItem('userId', firestoreUser.id || firestoreUser.firebaseUid || firestoreUser.username);
              if (firestoreUser.email) {
                sessionStorage.setItem('userEmail', firestoreUser.email);
              }
              if (userRole) {
                sessionStorage.setItem('userRole', userRole);
              }
              
              console.log('✅ Login successful via Firestore fallback, redirecting...');
              setIsLoading(false);
              onLogin();
              return;
            } else {
              console.error('❌ Password mismatch in Firestore:', {
                storedPassword: firestoreUser.password ? '***' : 'NOT SET',
                enteredPassword: password ? '***' : 'NOT SET',
                passwordsMatch: firestoreUser.password === password
              });
              setError('Incorrect password. Please try again.');
              setIsLoading(false);
              return;
            }
          } else {
            console.error('❌ User not found in Firestore for fallback login');
          }
        }
        
        // Handle specific Firebase error codes
        let errorMessage = 'Login failed. Please try again.';
        
        switch (firebaseError.code) {
          case 'auth/user-not-found':
            errorMessage = 'User not found. Please check your username/email or contact your administrator.';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password. Please try again.';
            break;
          case 'auth/invalid-credential':
            errorMessage = 'Invalid credentials. Please check your username and password.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email format. Please use a valid email address.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This account has been disabled. Please contact your administrator.';
            break;
          case 'auth/too-many-requests':
            // Silently ignore rate limit errors - don't show error message to user
            // Allow them to continue trying without blocking
            setError('');
            setIsLoading(false);
            return;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection and try again.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Email/password authentication is not enabled. Please contact your administrator.';
            break;
          default:
            errorMessage = `Login failed: ${firebaseError.message || firebaseError.code || 'Unknown error'}`;
        }
        
        setError(errorMessage);
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {showDesktopMessage && (
        <div className="login-desktop-message">
          <div className="desktop-message-content">
            <div className="desktop-message-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="12" rx="2"/>
                <path d="M8 18h8"/>
                <path d="M12 16v2"/>
              </svg>
            </div>
            <div className="desktop-message-text">
              <h4>For an optimal experience, use a desktop</h4>
              <p>While you can continue on mobile, desktop provides the best experience with all features.</p>
            </div>
            <button 
              className="desktop-message-close"
              onClick={() => setShowDesktopMessage(false)}
              aria-label="Close message"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 4L4 12M4 4l8 8"/>
              </svg>
            </button>
          </div>
        </div>
      )}
      <div className="login-box">
        <div className="login-header">
          <div className="login-logo">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Company Logo" 
                className="login-logo-image"
                onError={(e) => {
                  // Fallback to Plutoo SVG logo if image fails to load
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.plutoo-logo-svg-fallback');
                  if (fallback) fallback.style.display = 'block';
                }}
              />
            ) : null}
            <div 
              className="plutoo-logo-svg-fallback" 
              style={{ display: logoUrl ? 'none' : 'block', width: '100%', height: '100%' }}
            >
              <PlutooLogoSVG 
                className="login-logo-image" 
              />
            </div>
          </div>
          <p className="login-subtitle">Garage &amp; Dealer Management</p>
        </div>
        {error && (
          <div className={`login-error ${error ? 'show' : ''}`}>
            {error}
          </div>
        )}
        <MobileFormEnhancements>
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username" className="form-label">Username or Email</label>
              <input
                type="text"
                id="username"
                className="form-input"
                placeholder="Enter your username or email"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
                required
                autoComplete="username"
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="form-input password-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="form-group remember-me-group">
              <label className="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                  className="remember-me-checkbox"
                />
                <span>Remember me</span>
              </label>
            </div>
            <button type="submit" className="login-btn" disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </MobileFormEnhancements>
      </div>
    </div>
  );
};

export default Login;


